import { ComponentPropsWithoutRef, ElementType, ReactNode, SyntheticEvent, useState } from 'react';
import { getImageSrc } from 'utils/image.util';

type AppImageOwnProps = {
   src?: string | null;
   /** Supplier size to request, e.g. '640x400' or 'x500'. See `resolveImageSize`. */
   size?: string;
   /** Value for the `s=` segment when it differs from `size`. */
   querySize?: string;
   /**
    * What to render when there is no usable image. Defaults to nothing at all —
    * pass `<ImagePlaceholder />` only where a fixed frame needs its space held.
    */
   fallback?: ReactNode;
   /** Required so migrating a raw <img> can never silently drop its alt text. */
   alt: string;
};

/**
 * Polymorphic in `component` so the galleries that animate the picture itself
 * can pass `motion.img` and keep their framer-motion props type-checked.
 */
export type AppImageProps<C extends ElementType = 'img'> = AppImageOwnProps & {
   component?: C;
} & Omit<ComponentPropsWithoutRef<C>, keyof AppImageOwnProps | 'component'>;

/**
 * The single way this app renders a data-driven image.
 *
 * Two failure modes produced the broken-image glyphs users kept reporting: a
 * URL that is missing/empty/still a `{size}` template, and a URL that 404s or
 * times out at the supplier. Both resolve to the same answer here — render the
 * `fallback`, which is nothing unless the caller asks otherwise. We never
 * substitute a stock photo or a brand logo for content that isn't there.
 *
 * Static, bundled artwork (marketing backgrounds, empty-state illustrations)
 * does not belong here: it ships with the app and cannot go missing.
 */
const AppImage = <C extends ElementType = 'img'>({
   src,
   size,
   querySize,
   fallback = null,
   component,
   ...props
}: AppImageProps<C>) => {
   // Keyed by URL rather than a boolean: a new `src` is then automatically
   // un-failed, with no effect to sync and nothing to reset on the SSR pass.
   const [failedSrc, setFailedSrc] = useState<string | null>(null);

   const resolved = getImageSrc(src, size, querySize);

   if (!resolved || resolved === failedSrc) return <>{fallback}</>;

   const { onError, ...rest } = props as { onError?: (event: SyntheticEvent<HTMLImageElement>) => void };
   const Component = (component ?? 'img') as ElementType;

   return (
      <Component
         {...rest}
         src={resolved}
         onError={(event: SyntheticEvent<HTMLImageElement>) => {
            setFailedSrc(resolved);
            onError?.(event);
         }}
      />
   );
};

export default AppImage;
